/**
 * ProjectTasks Component (Dynamic)
 * 
 * This component handles task management and can work in TWO modes:
 * 
 * MODE 1 - PROJECT SCOPE (Project Details Section):
 * - Shows "Project Tasks" or custom project name in heading
 * - Project is pre-selected and locked
 * - Filters/modals don't show project selector
 * - Usage: <ProjectTasks projectId="123" projectName="My Project" isProjectScope={true} />
 * 
 * MODE 2 - GLOBAL SCOPE (Tasks Module):
 * - Shows "Tasks" in heading
 * - User can select any project
 * - All filters and modals show project selector
 * - Usage: <ProjectTasks isProjectScope={false} /> or <ProjectTasks />
 * 
 * @param {string} projectId - The ID of the project (required when isProjectScope=true)
 * @param {string} projectName - The name of the project (required when isProjectScope=true)
 * @param {boolean} isProjectScope - If true, component works in project mode; if false/undefined, works in global mode
 */

import { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import {
    FilterTasksOffcanvas,
    AddTaskModal,
    MoveOrCopyTaskModal,
    LogTimeModal,
    QuickAddSubtasksModal,
    DeleteTaskModal,
    TasksTable,
    Pagination,
    ViewHistoryModal,
    EstimateTimeModal
} from '../../../../components/MyTasks/components';
import {
    priorityOptions,
    privacyOptions,
    assigneeOptions,
    taskListOptions,
    stageOptions
} from '../../../../components/MyTasks/constants/options';

const ProjectTasks = ({ 
    projectId: propProjectId, 
    projectName: propProjectName,
    isProjectScope = false,
    taskPrefix = null
}) => {
    // ==================== Project Context ====================
    const isInProjectScope = isProjectScope || !!propProjectId;
    const projectId = isInProjectScope ? (propProjectId || '12345') : null;
    const projectName = isInProjectScope ? (propProjectName || 'eCommerce Website') : null;
    const pageHeading = isInProjectScope ? `Tasks for ${projectName}` : 'Tasks';
    
    const getTaskPrefix = () => {
        if (taskPrefix) return taskPrefix;
        if (isInProjectScope) return 'Project Task';
        return null;
    };

    /**
     * Clean task name by removing repeated words
     * Example: "Project Task - Project Task - Task 1" → "Project Task - Task 1"
     * @param {string} taskName - The task name to clean
     * @returns {string} - Clean task name without repeated words
     */
    const cleanTaskName = (taskName) => {
        if (!taskName) return '';
        
        // Split by common separators (-, |, /, etc.)
        const parts = taskName.split(/\s*[-|/]\s*/);
        
        // Track seen words (case-insensitive) to remove duplicates
        const seenWords = new Set();
        const cleanedParts = [];
        
        for (const part of parts) {
            const normalizedPart = part.trim().toLowerCase();
            
            if (normalizedPart && !seenWords.has(normalizedPart)) {
                seenWords.add(normalizedPart);
                cleanedParts.push(part.trim());
            }
        }
        
        // Rejoin with consistent separator
        return cleanedParts.join(' - ');
    };

    // ==================== Modal States ====================
    const [show, setShow] = useState(false); // Add/Edit Task Modal
    const [showDelete, setShowDelete] = useState(false); // Delete Task Modal
    const [showFilter, setShowFilter] = useState(false); // Filter Offcanvas
    const [showMoveOrCopy, setShowMoveOrCopy] = useState(false); // Move/Copy Modal
    const [showLogTime, setShowLogTime] = useState(false); // Log Time Modal
    const [showQuickSubtasks, setShowQuickSubtasks] = useState(false); // Quick Add Subtasks Modal
    const [showHistory, setShowHistory] = useState(false); // View History Modal
    const [showEstimateTime, setShowEstimateTime] = useState(false); // Estimate Time Modal

    // ==================== Task Form States ====================
    const [selectedTaskList, setSelectedTaskList] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [dueDate, setDueDate] = useState(new Date());
    const [estimatedTime, setEstimatedTime] = useState('');
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedPrivacy, setSelectedPrivacy] = useState(null);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [description, setDescription] = useState('');
    const [notifyByEmail, setNotifyByEmail] = useState(false);

    // ==================== Filter States (Project-Specific) ====================
    const [taskNameFilter, setTaskNameFilter] = useState('');
    const [taskListFilter, setTaskListFilter] = useState(null);
    const [assigneeFilter, setAssigneeFilter] = useState([]);
    const [dueDateRange, setDueDateRange] = useState([null, null]);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [stageFilter, setStageFilter] = useState(null);

    // ==================== Move/Copy States ====================
    const [moveOrCopyType, setMoveOrCopyType] = useState('move'); // 'move' or 'copy'
    const [targetTaskList, setTargetTaskList] = useState(null);

    // ==================== Log Time States ====================
    const [logDate, setLogDate] = useState(new Date());
    const [logTime, setLogTime] = useState('');
    const [logDescription, setLogDescription] = useState('');

    // ==================== Subtasks States ====================
    const [subtasksList, setSubtasksList] = useState('');

    // ==================== History States ====================
    const [historyTab, setHistoryTab] = useState('all');
    const [historyList, setHistoryList] = useState([
        {
            date: '12 Aug 2025',
            time: '10:30 AM',
            user: 'John Doe',
            action: 'Changed status from "In Progress" to "Completed"',
            type: 'status'
        },
        {
            date: '12 Aug 2025',
            time: '09:45 AM',
            user: 'John Doe',
            action: 'Logged 3 hours: Development of API endpoints',
            type: 'time'
        }
    ]);

    // ==================== Pagination States ====================
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0); // This should come from API
    const totalPages = Math.ceil(totalItems / pageSize);

    // ==================== Task Data States ====================
    const [tasks, setTasks] = useState([]); // Store fetched tasks
    const [loading, setLoading] = useState(false); // Loading state

    // ==================== Effects ====================
    /**
     * Fetch tasks for the specific project when component mounts or projectId changes
     */
    useEffect(() => {
        if (isInProjectScope && projectId) {
            fetchProjectTasks();
        } else if (!isInProjectScope) {
            fetchGlobalTasks();
        }
    }, [projectId, currentPage, pageSize, isInProjectScope]);

    // ==================== API Functions ====================
    /**
     * Fetch tasks specific to this project (PROJECT SCOPE MODE)
     * This ensures data comes from project-specific API endpoint
     * TODO: Replace with actual API call
     */
    const fetchProjectTasks = async () => {
        setLoading(true);
        try {
            console.log(`✅ FETCHING PROJECT-SPECIFIC TASKS for projectId: ${projectId}`);
            
            // TODO: Replace with actual API call
            // Import project tasks data for demo
            // import { projectTasks } from '../../../components/MyTasks/constants/taskData';
            // const tasksData = projectTasks.home; // or based on current page
            
            const mockProjectTasks = [
                { 
                    id: 1, 
                    name: 'Task 1',
                    taskList: 'Development',
                    subtasks: 3,
                    status: 'In Progress', 
                    priority: 'High', 
                    startDate: '01 Aug 2025',
                    startTime: '10:00 AM',
                    dueDate: '10 Aug 2025',
                    dueTime: '06:00 PM',
                    estimatedTime: '12h 30m',
                    loggedTime: '5h',
                    project: projectName || 'eCommerce Website',
                    projectDetails: 'Client: Netclues Technologies | PM: Sarah Johnson',
                    projectId: projectId,
                    subtasksList: []
                },
                { 
                    id: 2, 
                    name: 'Task 2',
                    taskList: 'UI/UX',
                    subtasks: 2,
                    status: 'Pending', 
                    priority: 'Medium', 
                    startDate: '05 Aug 2025',
                    startTime: '09:00 AM',
                    dueDate: '12 Aug 2025',
                    dueTime: '05:00 PM',
                    estimatedTime: '8h',
                    loggedTime: '1h',
                    project: projectName || 'eCommerce Website',
                    projectDetails: 'Client: Netclues Technologies | PM: Sarah Johnson',
                    projectId: projectId,
                    subtasksList: []
                },
                { 
                    id: 3, 
                    name: 'Task 3',
                    taskList: 'Testing',
                    subtasks: 4,
                    status: 'Completed', 
                    priority: 'Low', 
                    startDate: '10 Aug 2025',
                    startTime: '10:00 AM',
                    dueDate: '20 Aug 2025',
                    dueTime: '06:00 PM',
                    estimatedTime: '16h',
                    loggedTime: '16h',
                    project: projectName || 'eCommerce Website',
                    projectDetails: 'Client: Netclues Technologies | PM: Sarah Johnson',
                    projectId: projectId,
                    subtasksList: []
                },
            ];
            
            const prefix = getTaskPrefix();
            const formattedTasks = mockProjectTasks.map(task => {
                const rawName = prefix ? `${prefix} - ${task.name}` : task.name;
                return {
                    ...task,
                    name: cleanTaskName(rawName),
                    source: 'PROJECT_SPECIFIC'
                };
            });
            
            setTasks(formattedTasks);
            setTotalItems(100);
            
            console.log(`✅ PROJECT TASKS LOADED:`, formattedTasks);
        } catch (error) {
            console.error('Error fetching project tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch global tasks (GLOBAL SCOPE MODE)
     * This fetches tasks from all projects
     * TODO: Replace with actual API call
     */
    const fetchGlobalTasks = async () => {
        setLoading(true);
        try {
            console.log(`✅ FETCHING GLOBAL TASKS (all projects)`);
            
            // TODO: Replace with actual API call
            // Import global tasks data for demo
            // import { globalTasks } from '../../../components/MyTasks/constants/taskData';
            
            const mockGlobalTasks = [
                { 
                    id: 1, 
                    name: 'Backend Tasks',
                    taskList: 'Development',
                    subtasks: 3,
                    status: 'In Progress', 
                    priority: 'High',
                    startDate: '01 Aug 2025',
                    startTime: '10:00 AM',
                    dueDate: '15 Aug 2025',
                    dueTime: '06:00 PM',
                    estimatedTime: '12h 30m',
                    loggedTime: '5h 30m',
                    project: 'Global Project Management',
                    projectDetails: 'Sales: Kartik Mehta | TL: John Smith',
                    projectId: 'proj1',
                    subtasksList: []
                },
                { 
                    id: 2, 
                    name: 'Frontend Tasks',
                    taskList: 'UI/UX',
                    subtasks: 5,
                    status: 'Planning', 
                    priority: 'Medium',
                    startDate: '05 Aug 2025',
                    startTime: '09:00 AM',
                    dueDate: '20 Aug 2025',
                    dueTime: '05:00 PM',
                    estimatedTime: '20h',
                    loggedTime: '2h',
                    project: 'Global Project Management',
                    projectDetails: 'Sales: Kartik Mehta | TL: John Smith',
                    projectId: 'proj2',
                    subtasksList: []
                },
                { 
                    id: 3, 
                    name: 'Testing & QA',
                    taskList: 'Quality Assurance',
                    subtasks: 4,
                    status: 'Pending', 
                    priority: 'High',
                    startDate: '10 Aug 2025',
                    startTime: '10:00 AM',
                    dueDate: '25 Aug 2025',
                    dueTime: '06:00 PM',
                    estimatedTime: '16h',
                    loggedTime: '0m',
                    project: 'Global Project Management',
                    projectDetails: 'Sales: Kartik Mehta | TL: John Smith',
                    projectId: 'proj3',
                    subtasksList: []
                },
            ];
            
            const formattedTasks = mockGlobalTasks.map(task => ({
                ...task,
                name: cleanTaskName(task.name),
                source: 'GLOBAL'
            }));
            
            setTasks(formattedTasks);
            setTotalItems(100);
            
            console.log(`✅ GLOBAL TASKS LOADED:`, formattedTasks);
        } catch (error) {
            console.error('Error fetching global tasks:', error);
        } finally {
            setLoading(false);
        }
    };

    // ==================== Task Modal Handlers ====================
    /**
     * Open Add Task Modal
     */
    const handleShow = () => setShow(true);

    /**
     * Close Add Task Modal and reset form
     */
    const handleClose = () => {
        setShow(false);
        resetTaskForm();
    };

    /**
     * Reset task form to default values
     */
    const resetTaskForm = () => {
        setSelectedTaskList(null);
        setStartDate(new Date());
        setDueDate(new Date());
        setEstimatedTime('');
        setSelectedPriority(null);
        setSelectedPrivacy(null);
        setSelectedAssignees([]);
        setDescription('');
        setNotifyByEmail(false);
    };

    const handleTaskSubmit = () => {
        const taskData = {
            projectId,
            taskListId: selectedTaskList,
            startDate,
            dueDate,
            estimatedTime,
            priority: selectedPriority,
            privacy: selectedPrivacy,
            assignees: selectedAssignees,
            description,
            notifyByEmail
        };
        console.log('Creating task:', taskData);
        addToHistory('Created new task');
        handleClose();
        fetchProjectTasks();
    };

    // ==================== Delete Modal Handlers ====================
    const handleDeleteShow = () => setShowDelete(true);
    
    const handleDeleteClose = () => setShowDelete(false);
    
    const handleDelete = () => {
        console.log('Deleting task');
        addToHistory('Deleted task');
        handleDeleteClose();
        fetchProjectTasks();
    };

    // ==================== Move/Copy Modal Handlers ====================
    /**
     * Open Move/Copy Modal
     */
    const handleMoveOrCopyShow = () => setShowMoveOrCopy(true);

    /**
     * Close Move/Copy Modal and reset
     */
    const handleMoveOrCopyClose = () => {
        setShowMoveOrCopy(false);
        setMoveOrCopyType('move');
        setTargetTaskList(null);
    };

    const handleMoveOrCopySubmit = () => {
        const targetListName = taskListOptions.find(t => t.value === targetTaskList)?.label || 'selected list';
        const action = moveOrCopyType === 'move' ? 'Moved' : 'Copied';
        
        console.log(`${action} task to task list: ${targetTaskList}`);
        addToHistory(`${action} task to "${targetListName}"`);
        
        handleMoveOrCopyClose();
        fetchProjectTasks();
    };

    // ==================== Log Time Modal Handlers ====================
    /**
     * Open Log Time Modal
     */
    const handleLogTimeShow = () => setShowLogTime(true);

    /**
     * Close Log Time Modal and reset
     */
    const handleLogTimeClose = () => {
        setShowLogTime(false);
        setLogDate(new Date());
        setLogTime('');
        setLogDescription('');
    };

    /**
     * Submit time log entry
     * TODO: Implement API call
     */
    const handleLogTimeSubmit = () => {
        const timeLogData = {
            projectId,
            date: logDate,
            hours: logTime,
            description: logDescription
        };
        
        console.log('Logging time:', timeLogData);
        addToHistory(`Logged ${logTime} hours${logDescription ? `: ${logDescription}` : ''}`);
        
        handleLogTimeClose();
    };

    // ==================== Subtasks Modal Handlers ====================
    /**
     * Open Quick Add Subtasks Modal
     */
    const handleQuickSubtasksShow = () => setShowQuickSubtasks(true);
    
    /**
     * Close Quick Add Subtasks Modal and reset
     */
    const handleQuickSubtasksClose = () => {
        setShowQuickSubtasks(false);
        setSubtasksList('');
    };
    
    /**
     * Submit multiple subtasks at once
     * TODO: Implement API call
     */
    const handleQuickSubtasksSubmit = () => {
        // Split the text into individual subtasks, filtering out empty lines
        const subtasks = subtasksList
            .split('\n')
            .map(task => task.trim())
            .filter(task => task.length > 0);
            
        if (subtasks.length > 0) {
            console.log('Creating subtasks:', subtasks);
            addToHistory(`Added ${subtasks.length} new subtask${subtasks.length > 1 ? 's' : ''}`);
            handleQuickSubtasksClose();
            fetchProjectTasks(); // Refresh task list
        }
    };

    // ==================== History Modal Handlers ====================
    /**
     * Open View History Modal
     */
    const handleHistoryShow = () => {
        // In a real app, fetch history from API
        addToHistory('Viewed task history');
        setShowHistory(true);
    };

    /**
     * Close View History Modal
     */
    const handleHistoryClose = () => setShowHistory(false);

    // ==================== Estimate Time Modal Handlers ====================
    /**
     * Open Estimate Time Modal
     */
    const handleEstimateTimeShow = () => setShowEstimateTime(true);

    /**
     * Close Estimate Time Modal
     */
    const handleEstimateTimeClose = () => setShowEstimateTime(false);

    // ==================== Utility Functions ====================
    /**
     * Add entry to history list
     * @param {string} action - The action description
     */
    const addToHistory = (action) => {
        const newEntry = {
            date: new Date().toLocaleDateString(),
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            user: 'Current User', // TODO: Get from auth context
            action,
            type: 'action'
        };
        setHistoryList([newEntry, ...historyList]);
    };

    /**
     * Apply filters to task list
     * TODO: Implement API call with filters
     */
    const applyFilters = () => {
        console.log('Applying filters:', {
            projectId,
            taskName: taskNameFilter,
            taskList: taskListFilter,
            assignees: assigneeFilter,
            dateRange: dueDateRange,
            priority: priorityFilter,
            stage: stageFilter
        });
        setCurrentPage(1); // Reset to first page
        
        // Fetch tasks based on scope
        if (isInProjectScope) {
            fetchProjectTasks();
        } else {
            fetchGlobalTasks();
        }
    };

    /**
     * Clear all filters
     */
    const clearFilters = () => {
        setTaskNameFilter('');
        setTaskListFilter(null);
        setAssigneeFilter([]);
        setDueDateRange([null, null]);
        setPriorityFilter(null);
        setStageFilter(null);
        
        // Fetch tasks based on scope
        if (isInProjectScope) {
            fetchProjectTasks();
        } else {
            fetchGlobalTasks();
        }
    };

    // ==================== Render ====================
    return (
        <>
            {/* Task List Card */}
            <div className='c-card-1'>
                <div className='c-card-header-1'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <div>
                            {pageHeading}
                            {!isInProjectScope && (
                                <span className='badge bg-info ms-2' style={{fontSize: '0.7rem', verticalAlign: 'middle'}}>
                                    Global Tasks
                                </span>
                            )}
                        </div>
                        <div className='d-flex align-items-center gap-1'>
                            <Button 
                                variant='outline-primary' 
                                className='btn-sm me-2 text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                            <Button 
                                variant='primary' 
                                onClick={handleShow}
                                className='btn-sm text-nowrap'
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add Task
                            </Button>
                        </div>
                    </div>
                </div>
                
                <div className='c-card-body-1 p-0'>
                    {loading ? (
                        <div className='text-center p-5'>
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading tasks...</span>
                            </div>
                            <p className='mt-3 text-muted'>
                                {isInProjectScope 
                                    ? `Loading tasks for ${projectName}...` 
                                    : 'Loading all tasks...'}
                            </p>
                        </div>
                    ) : (
                        <TasksTable
                            tasks={tasks}
                            onEdit={handleShow}
                            onMoveOrCopy={handleMoveOrCopyShow}
                            onLogTime={handleLogTimeShow}
                            onViewHistory={handleHistoryShow}
                            onAddSubtasks={handleQuickSubtasksShow}
                            onView={() => {}}
                            onDuplicate={() => {}}
                            onDelete={handleDeleteShow}
                            onEstimateTime={handleEstimateTimeShow}
                        />
                    )}
                </div>
                
                <div className='c-card-footer-1'>
                    <Pagination
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                    />
                </div>
            </div>

            <AddTaskModal
                show={show}
                onHide={handleClose}
                selectedProject={projectId}
                setSelectedProject={() => {}}
                selectedTaskList={selectedTaskList}
                setSelectedTaskList={setSelectedTaskList}
                startDate={startDate}
                setStartDate={setStartDate}
                dueDate={dueDate}
                setDueDate={setDueDate}
                estimatedTime={estimatedTime}
                setEstimatedTime={setEstimatedTime}
                selectedPriority={selectedPriority}
                setSelectedPriority={setSelectedPriority}
                selectedPrivacy={selectedPrivacy}
                setSelectedPrivacy={setSelectedPrivacy}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                description={description}
                setDescription={setDescription}
                notifyByEmail={notifyByEmail}
                setNotifyByEmail={setNotifyByEmail}
                projectOptions={[]}
                taskListOptions={taskListOptions}
                priorityOptions={priorityOptions}
                privacyOptions={privacyOptions}
                assigneeOptions={assigneeOptions}
                isProjectLocked={true}
            />

            <DeleteTaskModal
                show={showDelete}
                onHide={handleDeleteClose}
                onConfirm={handleDelete}
            />

            <LogTimeModal
                show={showLogTime}
                onHide={handleLogTimeClose}
                logDate={logDate}
                setLogDate={setLogDate}
                logTime={logTime}
                setLogTime={setLogTime}
                logDescription={logDescription}
                setLogDescription={setLogDescription}
                onSubmit={handleLogTimeSubmit}
            />

            {/* Move/Copy Task Modal - Within project only */}
            <MoveOrCopyTaskModal
                show={showMoveOrCopy}
                onHide={handleMoveOrCopyClose}
                moveOrCopyType={moveOrCopyType}
                setMoveOrCopyType={setMoveOrCopyType}
                targetProject={projectId} // Fixed to current project
                setTargetProject={() => {}} // Disabled for project-specific view
                targetTaskList={targetTaskList}
                setTargetTaskList={setTargetTaskList}
                projectOptions={[]} // Empty - project is locked
                taskListOptions={taskListOptions}
                onSubmit={handleMoveOrCopySubmit}
                isProjectLocked={true} // Flag to hide project selector
            />

            {/* Filter Offcanvas - No project filter */}
            <FilterTasksOffcanvas
                show={showFilter}
                onHide={() => setShowFilter(false)}
                taskNameFilter={taskNameFilter}
                setTaskNameFilter={setTaskNameFilter}
                projectFilter={projectId} // Fixed to current project
                setProjectFilter={() => {}} // Disabled for project-specific view
                taskListFilter={taskListFilter}
                setTaskListFilter={setTaskListFilter}
                assigneeFilter={assigneeFilter}
                setAssigneeFilter={setAssigneeFilter}
                dueDateRange={dueDateRange}
                setDueDateRange={setDueDateRange}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                stageFilter={stageFilter}
                setStageFilter={setStageFilter}
                projectOptions={[]} // Empty - project is locked
                taskListOptions={taskListOptions}
                assigneeOptions={assigneeOptions}
                priorityOptions={priorityOptions}
                stageOptions={stageOptions}
                hideProjectFilter={true} // Flag to hide project filter
                onApply={applyFilters}
                onClear={clearFilters}
            />

            {/* Quick Add Subtasks Modal */}
            <QuickAddSubtasksModal
                show={showQuickSubtasks}
                onHide={handleQuickSubtasksClose}
                subtasksList={subtasksList}
                setSubtasksList={setSubtasksList}
                onSubmit={handleQuickSubtasksSubmit}
            />

            {/* View History Modal */}
            <ViewHistoryModal
                show={showHistory}
                onHide={handleHistoryClose}
                historyTab={historyTab}
                setHistoryTab={setHistoryTab}
                historyList={historyList}
            />

            {/* Estimate Time Modal */}
            <EstimateTimeModal
                show={showEstimateTime}
                onHide={handleEstimateTimeClose}
            />
        </>
    );
};

export default ProjectTasks;
